import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env";
import { Banner, BannerActionType } from "@types";
import {
  BehaviorSubject,
  EMPTY,
  fromEvent,
  interval,
  merge,
  Observable,
} from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
} from "rxjs/operators";
import { BannerService } from "./banner.service";

interface VersionManifest {
  appVersion: string;
  buildNumber: string;
  buildSourceVersion: string;
}

const pollIntervalMilliseconds = 15 * 60 * 1000;

const updateAvailableBanner: Banner = {
  id: "appUpdateAvailable",
  content:
    "A new version of Celo is available. Refresh the page to get the latest version.",
  actions: [
    {
      displayText: "Update",
      type: BannerActionType.Reload,
      value: "",
      order: 0,
    },
  ],
  order: -1,
  category: 1,
  isDismissible: true,
  dismissPersistence: "none",
};

@Injectable({
  providedIn: "root",
})
export class AppVersionService {
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  public updateAvailable$ = this.updateAvailableSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  public constructor(
    private http: HttpClient,
    private bannerService: BannerService
  ) {}

  /**
   * Begins polling the deployed version manifest for a newer version than the one currently
   * loaded. Should only be called once per app lifetime.
   */
  public startPolling(): void {
    const trigger$ = merge(
      interval(pollIntervalMilliseconds),
      fromEvent(document, "visibilitychange").pipe(
        filter(() => document.visibilityState === "visible")
      )
    ).pipe(startWith(0));

    trigger$
      .pipe(
        // No need to keep checking once we already know about an available update.
        filter(() => !this.updateAvailableSubject.value),
        switchMap(() => this.checkForUpdate())
      )
      .subscribe();
  }

  private checkForUpdate(): Observable<void> {
    // environment.appVersion is prefixed (e.g. "web-1.2.3"); version.json only contains the raw version.
    const currentVersion = environment.appVersion.replace(/^web-/, "");

    // Cache-bust so the browser/CDN doesn't serve back the manifest this client was built with.
    return this.http
      .get<VersionManifest>(`/assets/version.json?t=${Date.now()}`)
      .pipe(
        map((manifest) => {
          // This won't property handle rollbacks, but that's fine.
          if (manifest.appVersion === currentVersion) return;
          this.updateAvailableSubject.next(true);
          this.bannerService.addBanner(updateAvailableBanner);
        }),
        catchError(() => EMPTY)
      );
  }
}
